// import ABTestHome from 'components/ABTestHome'
import { getTranslationFromJson } from '@patrianna/shared-utils/prismic'
import { useSearchParams } from 'next/navigation'
import { Fragment, useEffect } from 'react'

import JackpotBadge from 'components/JackpotBadge'
import Providers from 'components/Providers'
import AnimateWhenCurrencyChange from 'containers/AnimateWhenCurrencyChange'
import HomeBanners from 'containers/HomeBanners'
import { shouldShowNotAvailableLocation } from 'pageContainers/Home/components/NotAvailableLocation/utils/shouldShowNotAvailableLocation'
import PreviouslyPlayedRow from 'pageContainers/Home/components/PreviouslyPlayedRow'
import content from 'prismic-translates/page-confirm-email.json'
import { IS_BRAND_PULSZBINGO } from 'src/config/brands'
import BingoRooms from 'src/containers/Bingo/BingoRooms'
import WidgetsBar from 'src/containers/WidgetsBar'
import { useAutoCloseSnackbarByTime } from 'src/hooks/useAutoCloseSnackbarByTime'
import { favoritesGamesSelector } from 'src/store/modules/favorites/selectors'
import { isLoggedInSelector } from 'src/store/modules/user/selectors'
import {
  getDataFromSessionStorage,
  removeDataFromSessionStorage,
  SKIP_HOME_JACKPOT_FETCH_ONCE,
} from 'src/utils/sessionStorage'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { getCategoriesWith3DImages, isWidgetsBarEnabledSelector } from 'store/modules/brandConfig/selectors'
import { getActiveCurrencyIdSelector } from 'store/modules/currencies/selectors'
import { geoBlockedGameCodesSelector } from 'store/modules/games/geoBlockedContentSelectors'
import { filterGeoBlockedGames } from 'store/modules/games/utils'
import { getJackpotTotals, getPersonalJackpot } from 'store/modules/jackpots/actions'
import {
  getJackpotPersonalExpireSelector,
  showJackpotPersonalExpireBadgeSelector,
} from 'store/modules/jackpots/selectors'
import { openSnackbar } from 'store/modules/snackbar/actions'
import { filterBingoGamesByMode } from 'utils/filterBingoGamesByMode'

import NotAvailableLocation from '../NotAvailableLocation'
import SeoContent from '../SeoContent'
import SlotsRow from '../SlotsRow'

import classes from './styles.module.scss'

import type { ActiveRoom } from '@patrianna/shared-patrianna-types/store/BingoModule'
import type { SwimlaneCategory } from '@patrianna/shared-patrianna-types/store/GameModule'
import type { Provider } from '@patrianna/shared-patrianna-types/store/ProviderModule'

const t = getTranslationFromJson([content])

type Props = {
  categories: SwimlaneCategory[]
  cookies: string
  bingoRooms?: ActiveRoom[]
  providers?: Provider[]
  layout?: string
}

export default function HomeContent(props: Props) {
  const { categories: categoriesFromProps, cookies } = props
  // Temporary remove providers category from response
  const categories = Array.isArray(categoriesFromProps)
    ? categoriesFromProps.filter((category) => category.type !== 'providers')
    : []
  const showNotAvailableLocation = shouldShowNotAvailableLocation(categories)
  const personalExpire = useAppSelector(getJackpotPersonalExpireSelector)
  const showBadge = useAppSelector(showJackpotPersonalExpireBadgeSelector)
  const categoriesWith3DImage = useAppSelector(getCategoriesWith3DImages)
  const favoritesGames = useAppSelector(favoritesGamesSelector)
  const geoBlockedCodes = useAppSelector(geoBlockedGameCodesSelector)
  const loggedIn = useAppSelector(isLoggedInSelector)
  const isWidgetsBarEnabled = useAppSelector(isWidgetsBarEnabledSelector)
  const activeCurrency = useAppSelector(getActiveCurrencyIdSelector)
  const isOldStashEnabled = !isWidgetsBarEnabled && personalExpire && showBadge
  const query = useSearchParams()
  const dispatch = useAppDispatch()
  const alreadyConfirmedEmailMessage = t(`${process.env.BRAND_NAME}_already_confirm_email`)

  useEffect(() => {
    if (query.get('alreadyConfirmed')) {
      dispatch(
        openSnackbar({
          message: alreadyConfirmedEmailMessage,
          variant: 'info',
        }),
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Fetch fresh jackpot data when navigating to home page (e.g., after playing a game)
  // to ensure StashWidget displays with the latest contribution data
  useEffect(() => {
    if (loggedIn && isWidgetsBarEnabled) {
      const shouldSkip = getDataFromSessionStorage<boolean>(SKIP_HOME_JACKPOT_FETCH_ONCE)

      if (shouldSkip) {
        removeDataFromSessionStorage(SKIP_HOME_JACKPOT_FETCH_ONCE)

        return
      }

      dispatch(getPersonalJackpot())
      dispatch(getJackpotTotals())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useAutoCloseSnackbarByTime([alreadyConfirmedEmailMessage])
  const providersSwimlane = {
    title: 'Providers',
    route: 'providers',
    type: 'providers',
    icon: 'providers-icon.svg',
    products: props.providers,
  }
  const bingoSwimlane = {
    title: 'Play Bingo',
    route: '/bingo',
    type: 'bingo',
    icon: 'ticket.svg',
    products: [] as any,
  }
  // Insert provider swimlane on 4th position of swimlane response
  const categoriesWithProviders =
    categories.length < 2
      ? categories
      : [...categories.slice(0, 7), providersSwimlane as unknown as SwimlaneCategory, ...categories.slice(7)]
  // Insert bingo swimlane on 1st position of swimlane response, to handle the case where the bingo swimlane is not in the response
  const updatedCategories = IS_BRAND_PULSZBINGO
    ? [
        bingoSwimlane as unknown as SwimlaneCategory,
        ...categoriesWithProviders.filter((category) => category.type !== 'bingo'),
      ]
    : categoriesWithProviders

  return (
    <div className={classes.pageContent}>
      <HomeBanners cookies={cookies} />
      {/* <ABTestHome cookies={props.cookies} /> */}
      {loggedIn && isWidgetsBarEnabled && <WidgetsBar />}

      <AnimateWhenCurrencyChange disabledAtTime={2000}>
        {updatedCategories.map((category, categoryIndex) => {
          const is3DTilesEnabled = !!categoriesWith3DImage?.[category?.code]

          if (category.code === 'last_played') {
            // PLDV-8952: filtered before count/lines so a row of only geo-blocked
            // games disappears entirely instead of leaving an empty header.
            const visiblePlayedGames = filterGeoBlockedGames(category.products, geoBlockedCodes)

            return (
              <PreviouslyPlayedRow
                code={category.code}
                games={visiblePlayedGames}
                swimlaneIndex={categoryIndex + 1}
                title={category.title}
                iconImg={category.icon}
                count={visiblePlayedGames.length}
                url={category.route}
                lines={category.lines >= visiblePlayedGames.length ? 1 : category.lines}
                is3DTilesEnabled={is3DTilesEnabled}
                layout={props.layout}
                key={`${category.index}-${category.title}`}
              />
            )
          }

          if (category.type === 'bingo') {
            return (
              <Fragment key={`${category.index}-${category.title}`}>
                <BingoRooms
                  rooms={props.bingoRooms}
                  bingoProducts={category.products}
                  title={category.title}
                  iconImg={category.icon}
                  count={category.products.length}
                  url={category.route}
                  lines={category.lines >= category.products.length ? 1 : category.lines}
                  layout={props.layout}
                />
              </Fragment>
            )
          }

          if (category.type === 'providers') {
            return (
              <Fragment key={`${category.index}-${category.title}`}>
                <Providers
                  url={category.route}
                  title={category.title}
                  products={category.products as unknown as Provider[]}
                  iconImg={category.icon}
                />
              </Fragment>
            )
          }

          const categoryGames =
            category.code === 'favourite' && favoritesGames !== null ? favoritesGames : category.products
          // PLDV-8952: geo filter applied to every row list, so a fully blocked
          // category (favorites included) hides its header via SlotsRow's own check.
          const filteredGames = filterGeoBlockedGames(
            filterBingoGamesByMode(categoryGames, activeCurrency),
            geoBlockedCodes,
          )

          return (
            <Fragment key={`${category.index}-${category.title}`}>
              <SlotsRow
                code={category.code}
                games={filteredGames}
                swimlaneIndex={categoryIndex + 1}
                title={category.title}
                iconImg={category.icon}
                count={filteredGames.length}
                url={category.route}
                lines={category.lines >= filteredGames.length ? 1 : category.lines}
                is3DTilesEnabled={is3DTilesEnabled}
                layout={props.layout}
              />
            </Fragment>
          )
        })}
        {showNotAvailableLocation && (
          <div className={classes.not_available_location_container}>
            <NotAvailableLocation />
          </div>
        )}
      </AnimateWhenCurrencyChange>

      <div className={classes.seo}>
        <SeoContent />
        {isOldStashEnabled && <JackpotBadge personalExpire={personalExpire} />}
      </div>
    </div>
  )
}
